/**
 * Tracking de conversión — esqueleto con placeholders.
 *
 * Para activar realmente, completar lo siguiente:
 *   1. Reemplazar GA4_MEASUREMENT_ID en /app/frontend/public/index.html
 *      por el ID real (G-XXXXXXX) y descomentar el snippet de Google Tag Manager.
 *   2. (Opcional) Configurar GTM_CONTAINER_ID (GTM-XXXXXX) si se prefiere GTM.
 *   3. (Opcional) Reemplazar META_PIXEL_ID si se usa Facebook Pixel.
 *
 * Por ahora, todas las llamadas son "no-op seguras": no rompen si los scripts
 * no están cargados; sólo dejan rastro en console.debug cuando NODE_ENV=development.
 */

const isDev = process.env.NODE_ENV !== 'production';

// IDs placeholder — completar luego desde process.env.REACT_APP_GA4_ID etc.
const GA4_ID = process.env.REACT_APP_GA4_ID || ''; // ej: 'G-XXXXXXX'
const GTM_ID = process.env.REACT_APP_GTM_ID || ''; // ej: 'GTM-XXXXXX'
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || ''; // ej: '1234567890'

// Gating de consentimiento (Chile · Ley 21.719). Sólo emitimos tracking si el
// usuario aceptó cookies analíticas. Import perezoso para evitar ciclos.
function hasAnalyticsConsent() {
  try {
    // eslint-disable-next-line global-require
    const { hasAnalyticsConsent: check } = require('./consent');
    return check();
  } catch (_) { return false; }
}

/**
 * Envía un evento genérico a las plataformas configuradas (GA4 / GTM / Meta).
 *
 * @param {string} eventName - Nombre canónico del evento (ej. 'cta_click').
 * @param {object} params - Parámetros del evento (ej. { cta_id, location, label }).
 */
export const trackEvent = (eventName, params = {}) => {
  if (!eventName) return;

  const payload = {
    event: eventName,
    timestamp: new Date().toISOString(),
    ...params,
  };

  // Sin consentimiento explícito ⇒ no emitimos a redes de analítica/marketing.
  // Sólo dejamos rastro local en dev para debug.
  const consented = hasAnalyticsConsent();

  try {
    if (consented) {
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(payload);
      }
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      }
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('trackCustom', eventName, params);
      }
    }
  } catch (err) {
    // no-op
  }

  if (isDev) {
    // eslint-disable-next-line no-console
    console.debug('[tracking]', eventName, params, consented ? '(sent)' : '(gated: no-consent)');
  }
};

/**
 * Helper específico para CTAs (botones).
 * Uso: trackCTA('hero-cta-reunion', { label: 'Agendar reunión', location: 'hero' });
 */
export const trackCTA = (ctaId, params = {}) => {
  trackEvent('cta_click', { cta_id: ctaId, ...params });
};

/**
 * Helper para envíos de formulario.
 * Uso: trackFormSubmit('contacto', { motivo: 'piloto' });
 */
export const trackFormSubmit = (formId, params = {}) => {
  trackEvent('form_submit', { form_id: formId, ...params });
};

/**
 * Helper para navegación de sección (scroll a sección/anchor).
 */
export const trackNav = (targetId, params = {}) => {
  trackEvent('nav_click', { target: targetId, ...params });
};
